import { DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, Space, Select, Tag } from 'antd';
import { seasonService } from 'content/service';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { accountStore } from 'store/accountStore';
import { SeasonInterface, SeasonData } from 'type/seasonType';
import { LanguageCode, SUPPORTED_LANGUAGES } from '../../../../shared/constants/i18n/languages';
import {
  getTranslation,
  getSeasonSupportedLanguages,
} from '../../../../shared/services/i18n/i18nService';
import * as Component from '../../../../shared/components';
import SeasonBulkDistributionButton from '../components/main/SeasonBulkDistributionButton';
import SeasonBulkUpdateButton from '../components/main/SeasonBulkUpdateButton';
import SeasonHandlers from '../handlers/SeasonHandlers';

const Season: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    page_size: '100',
    status: 'draft,published',
  });
  const user = accountStore.getAccount();
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [selectedList, setSelectedList] = useState<number[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>('ko');
  const [_seasonData, setSeasonData] = useState<SeasonData[]>([]);
  const isPlatformOne = user?.platform.id === 1;

  const { onClickDeleteButton } = SeasonHandlers();
  const columns = [
    {
      title: 'POSTER',
      dataIndex: 'poster',
      key: 'poster',
      width: '15%',
      render: (_text: string, record: SeasonInterface): JSX.Element => (
        <img
          style={{ marginLeft: '10px', width: '100%', height: 'auto' }}
          src={record.poster?.file}
          alt=""
        />
      ),
    },
    {
      title: 'TITLE',
      dataIndex: 'title',
      key: 'title',
      width: '25%',
      sorter: (a: SeasonInterface, b: SeasonInterface): number => a.title.localeCompare(b.title),
      render: (text: string, record: SeasonData): JSX.Element => {
        // i18n 필드에서 선택된 언어의 제목 가져오기
        const displayTitle = getTranslation(record.i18n_title, selectedLanguage) || text;
        const supportedLanguages = getSeasonSupportedLanguages(record as any);
        const hasTranslation = record.i18n_title?.[selectedLanguage];

        return (
          <div>
            <a
              href="#"
              className="season-title"
              onClick={() => window.open(`/season/edit/${record.id}`, '_blank')}
              style={{
                color: hasTranslation ? '#1890ff' : '#999',
                fontStyle: hasTranslation ? 'normal' : 'italic',
              }}
            >
              {displayTitle}
            </a>
            <div style={{ marginTop: '4px' }}>
              {supportedLanguages.map(lang => (
                <Tag
                  key={lang}
                  color={lang === selectedLanguage ? 'blue' : 'default'}
                  style={{ fontSize: '10px' }}
                >
                  {SUPPORTED_LANGUAGES[lang]?.flag} {SUPPORTED_LANGUAGES[lang]?.label}
                </Tag>
              ))}
              {supportedLanguages.length === 0 && (
                <Tag color="red" style={{ fontSize: '10px' }}>
                  번역 없음
                </Tag>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'SLUG',
      dataIndex: 'slug',
      key: 'slug',
      width: '10%',
      sorter: (a: SeasonInterface, b: SeasonInterface): number => a.slug.localeCompare(b.slug),
      render: (_: string, record: SeasonInterface): JSX.Element => (
        <div
          className="season-title"
          onClick={() => window.open(`/season/edit/${record.id}`, '_blank')}
        >
          <div
            className="season-title"
            style={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {record.slug}
          </div>
        </div>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      width: '5%',
      filters: [
        {
          text: 'Draft',
          value: 'draft',
        },
        {
          text: 'Published',
          value: 'published',
        },
        {
          text: 'Unpublished',
          value: 'unpublished',
        },
      ],
      filteredValue: searchParams.get('status') ? searchParams.get('status')?.split(',') : null,
      render: (_: string, record: SeasonInterface): string => record.status,
    },
    {
      title: 'DUBBING MODE',
      dataIndex: 'dubbing_mode',
      key: 'dubbing_mode',
      width: '5%',
      filters: [
        {
          text: 'Inherit',
          value: 'inherit',
        },
        {
          text: 'Enabled',
          value: 'enabled',
        },
        {
          text: 'Disabled',
          value: 'disabled',
        },
      ],
      filteredValue: searchParams.get('dubbing_modes')
        ? searchParams.get('dubbing_modes')?.split(',')
        : null,
      render: (text: string): string => text,
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      dataIndex: 'actions',
      render: (_text: string, record: SeasonInterface): JSX.Element => (
        <Space>
          <Button
            icon={<SettingOutlined />}
            onClick={() => window.open(`/season/edit/${record.id}`, '_blank')}
            className="action-btn"
          />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => onClickDeleteButton(record)}
            className={record.is_deletable ? 'action-btn' : 'delete-btn'}
          />
        </Space>
      ),
    },
  ];

  const fetchSeasonTableData = useCallback(
    async (params: URLSearchParams): Promise<{ data: SeasonData[]; count: number }> => {
      try {
        // i18n 필드를 포함한 Season 데이터 조회
        const seasonList = await seasonService.fetchSeasonsWithI18n(params);
        setSeasonData(seasonList.results);
        return { data: seasonList.results, count: seasonList.count };
      } catch (error) {
        console.error('Failed to fetch seasons with i18n:', error);
        // 폴백으로 기존 API 사용
        const seasonList = await seasonService.fetch(params);
        return { data: seasonList.results as SeasonData[], count: seasonList.count };
      }
    },
    [searchParams, selectedLanguage]
  );

  useEffect(() => {
    setRefreshCounter(prev => prev + 1);
  }, [searchParams]);

  return (
    <Component.BasePageComponent>
      <Component.TableComponent<SeasonData>
        key={refreshCounter}
        name="Season"
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        dataFetch={fetchSeasonTableData}
        columns={columns}
        actions={{ onSelectionChange: setSelectedList, onAddClick: () => navigate('/season/add') }}
        additionalButton={
          [
            isPlatformOne ? SeasonBulkDistributionButton({ selectedList }) : null,
            SeasonBulkUpdateButton,
          ].filter(Boolean) as JSX.Element[]
        }
      />
    </Component.BasePageComponent>
  );
};

export default observer(Season);
